import React, { useState, useEffect } from 'react';
import { DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography,
    IconButton, Alert, CircularProgress, ButtonBase, InputAdornment,
} from '@mui/material';
import { Close, ContactPhone, CheckCircle, RadioButtonUnchecked, WhatsApp, Storefront } from '@mui/icons-material';
import { businessAPI } from '../api';
import { contactsSupported, pickOneContact } from '../utils/contacts';
import { normalizeMobile } from '../utils/mobile';
import { WHATSAPP } from '../theme/tokens';

/**
 * Send every product to another vendor. They get a notification in their app and choose which
 * ones to copy — names, photos, prices, delivery days and set-day plans. Never customers or bills.
 *
 * Pick them from the phone's contacts (Android) or type their number (iPhone has no picker).
 */
const ShareProductsDialog = ({ open, onClose, businessId, productCount }) => {
    const [mobile, setMobile] = useState('');
    const [contactName, setContactName] = useState('');
    const [finding, setFinding] = useState(false);
    const [found, setFound] = useState(null);        // null = not looked up yet; [] = not on Vendora
    const [chosenId, setChosenId] = useState(null);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(null);           // { to_business_name, product_count }
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setMobile(''); setContactName(''); setFound(null); setChosenId(null);
        setDone(null); setError(''); setFinding(false); setSending(false);
    }, [open]);

    const find = async (number) => {
        const n = normalizeMobile(number);
        if (n.length < 10) return;
        setFinding(true); setError(''); setFound(null); setChosenId(null);
        try {
            const res = await businessAPI.findVendorToShare(businessId, n);
            const list = res.data?.businesses || [];
            setFound(list);
            if (list.length === 1) setChosenId(list[0].f_id);
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not check this number. Please try again.');
        } finally {
            setFinding(false);
        }
    };

    const onType = (v) => {
        const digits = v.replace(/[^\d+]/g, '');
        setMobile(digits);
        setContactName('');
        setFound(null); setChosenId(null);
        if (normalizeMobile(digits).length === 10) find(digits);
    };

    const pickContact = async () => {
        try {
            const c = await pickOneContact();
            if (!c || !c.mobile) return;
            setMobile(c.mobile);
            setContactName(c.name);
            find(c.mobile);
        } catch { /* picker closed */ }
    };

    const share = async () => {
        if (!chosenId) return;
        setSending(true); setError('');
        try {
            const res = await businessAPI.shareProducts(businessId, chosenId);
            setDone(res.data);
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not share. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const tenDigits = normalizeMobile(mobile);
    const inviteText = `Hi${contactName ? ' ' + contactName : ''}! I use Vendora to manage my customers, plans and bills. `
        + `Join so I can share my products with you: https://vendora88.com`;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight={800}>Share your products</Typography>
                <IconButton onClick={onClose} edge="end" sx={{ width: 44, height: 44 }}><Close /></IconButton>
            </DialogTitle>

            <DialogContent>
                {done ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="body1" fontWeight={800}>Shared with {done.to_business_name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            They will see your {done.product_count} product{done.product_count !== 1 ? 's' : ''} in
                            their app and can choose which ones to add.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Send all {productCount} product{productCount !== 1 ? 's' : ''} — names, photos, prices
                            and delivery days — to another vendor. Your customers and bills are never shared.
                        </Typography>

                        {contactsSupported && (
                            <Button fullWidth variant="outlined" startIcon={<ContactPhone />} onClick={pickContact}
                                sx={{ minHeight: 48, mb: 1.5, textTransform: 'none', fontWeight: 700 }}>
                                Pick from contacts
                            </Button>
                        )}

                        <TextField fullWidth label={contactsSupported ? 'Or type their mobile number' : 'Their mobile number'}
                            value={mobile} onChange={(e) => onType(e.target.value)}
                            inputProps={{ inputMode: 'tel', maxLength: 15 }}
                            helperText={contactName || ' '}
                            InputProps={{ endAdornment: finding ? <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment> : null }} />

                        {found && found.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                    {found.length > 1 ? 'Which business?' : 'Send to'}
                                </Typography>
                                {found.map((b) => {
                                    const on = chosenId === b.f_id;
                                    return (
                                        <ButtonBase key={b.f_id} onClick={() => setChosenId(b.f_id)}
                                            sx={{ justifyContent: 'flex-start', gap: 1.25, px: 1.25, minHeight: 52, borderRadius: 1.5,
                                                  border: '1.5px solid', borderColor: on ? 'primary.main' : 'divider',
                                                  bgcolor: on ? 'rgba(25,118,210,0.06)' : 'transparent' }}>
                                            {on ? <CheckCircle color="primary" /> : <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />}
                                            <Storefront sx={{ color: 'text.secondary' }} />
                                            <Typography variant="body2" fontWeight={on ? 800 : 600}>{b.f_name}</Typography>
                                        </ButtonBase>
                                    );
                                })}
                            </Box>
                        )}

                        {found && found.length === 0 && (
                            <Box sx={{ mt: 0.5, p: 1.5, borderRadius: 2, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
                                <Typography variant="body2" fontWeight={700}>Not on Vendora yet</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Invite them. Once they join, share again.
                                </Typography>
                                <Button fullWidth variant="contained" disableElevation startIcon={<WhatsApp />}
                                    component="a" target="_blank" rel="noopener noreferrer"
                                    href={`https://wa.me/91${tenDigits}?text=${encodeURIComponent(inviteText)}`}
                                    sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700,
                                          bgcolor: WHATSAPP.main, '&:hover': { bgcolor: WHATSAPP.hover } }}>
                                    Invite on WhatsApp
                                </Button>
                            </Box>
                        )}

                        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                {done ? (
                    <Button fullWidth variant="contained" disableElevation onClick={onClose}
                        sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}>Done</Button>
                ) : (
                    <>
                        <Button variant="outlined" onClick={onClose} sx={{ minHeight: 48, textTransform: 'none' }}>Cancel</Button>
                        <Button variant="contained" disableElevation onClick={share} disabled={!chosenId || sending}
                            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ flex: 1, minHeight: 48, textTransform: 'none', fontWeight: 800 }}>
                            {sending ? 'Sharing…' : 'Share'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ShareProductsDialog;
